<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import axios from 'axios';
import BaseSpinner from '@/components/base/BaseSpinner.vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import { processUserPermissions } from '@/pages/VirtualTerminal-utils';
import { computedAsync } from '@vueuse/core';

const store = useStore();
const toast = useToast();
let toastdefault = {
  position: "top-center",
  timeout: 5000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: true,
  closeButton: "button",
  icon: true,
  rtl: false
}
const router = useRouter();
const route = useRoute();

/**
 * @type {import("vue").Ref<UnwrapRef<boolean | null>>}
 */
const isMissingRbacPermissions = ref(null);
const iframeLink = ref('');
const loading = ref(true);
const params = ref('');
const url = ref('');
const userId = ref('');
const merchantId = ref('');
/**
 * @type {import("vue").Ref<HTMLIFrameElement | null>}
 */
const paymentIframe = ref(null);
const hasPaymentSuccess = ref(false);

const backButtonLabel = computed(() => (hasPaymentSuccess.value ? 'Vault' : 'Back'));

/**
 * @typedef {{
 *   id: string
 *   order: int
 *   name: string
 *   type: string
 *   values?: Array<string>
 *   required: boolean
 *   is_hidden: boolean
 *   name: string
 * }} CustomField
 */

/**
 * @type {import("vue").Ref<Array<CustomField>>}
 */
const customFields = ref([]);

const user = computed(() => store.state.user);
const permissions = computed(() => store.state.permissions);
const merchantHasPermissions = computed(
  () => store.getters.merchantHasPermissions
);

const computedRoutePublicKey = computed(() => route.params.apikey);
const computedRouteApiKey = computed(() => route.query.routing);
const terminalId = computed(() => route.query.terminalId);

// New: Store the selected terminal info
const selectedTerminal = ref(null);
const availableTerminals = ref([]);

function handleBackClick() {
  if (hasPaymentSuccess.value) {
    router.push({ name: 'Vault' });
    return;
  }

  router.go(-1);
}

function getIframeOrigin() {
  if (!iframeLink.value) return null;
  try {
    return new URL(iframeLink.value).origin;
  } catch {
    return null;
  }
}

function handleIframeMessage(event) {
  let msg;
  try {
    msg = JSON.parse(event.data);
  } catch {
    return;
  }

  if (msg?.action === 'success') {
    hasPaymentSuccess.value = true;
    console.log('payment success', msg.payload);
  }
}

/**
 * @type {import("vue").Ref<{id: string, name: string} | null>}
 */
const computedMerchantFromApiKey = computedAsync(async () => {
  const response = await axios.get(`/api/whoami`, {
    headers: { Authorization: `Bearer ${computedRouteApiKey.value}` },
  });
  return response.data.message
}, null);

/**
 * @typedef {{
 *   isSwipeAndCNP: Boolean
 *   isSwipeOnly: Boolean
 *   customerId?: string
 *   paymentMethodId?: string
 *   patient_first_name?: string
 *   patient_last_name?: string
 *   patient_number?: string
 *   payment_method_ids?: string[]
 * }} RouteInfo
 */

/**
 * @type {import("vue").ComputedRef<RouteInfo>}
 */
const computedRouteInfo = computed(() => {
  const query = route.query;
  const firstQueryValue = (value) => (Array.isArray(value) ? value[0] : value);
  const queryValues = (value) => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item)).filter(Boolean);
    }
    if (value === undefined || value === null || value === '') {
      return [];
    }
    return [String(value)];
  };
  let cri = {
    isSwipeAndCNP: firstQueryValue(query.isSwipeAndCNP) === 'true',
    isSwipeOnly: firstQueryValue(query.isSwipeOnly) === 'true',
    customerId: firstQueryValue(query.customerId),
    paymentMethodId: firstQueryValue(query.paymentMethodId),
    patient_first_name: firstQueryValue(query.patient_first_name),
    patient_last_name: firstQueryValue(query.patient_last_name),
    patient_number: firstQueryValue(query.patient_number),
    payment_method_ids: queryValues(query.payment_method_ids),
  };
  console.log('we have new computedRouteInfo now: ', cri);
  return cri;
});

// noinspection JSValidateTypes
/**
 * @type {import("vue").ComputedRef<VaultPageCustomer>}
 */
const customerComputedAsync = computedAsync(async () => {
  console.log('customerComputedAsync')
  let customerId = computedRouteInfo.value.customerId;
  if (!customerId) return null;

  const vaultRequestParams = new URLSearchParams();
  const patientFirstName = computedRouteInfo.value.patient_first_name;
  const patientLastName = computedRouteInfo.value.patient_last_name;
  const patientNumber = computedRouteInfo.value.patient_number;
  const paymentMethodIds = computedRouteInfo.value.payment_method_ids || [];

  if (patientFirstName) {
    vaultRequestParams.append('patient_first_name', patientFirstName);
  }
  if (patientLastName) {
    vaultRequestParams.append('patient_last_name', patientLastName);
  }
  if (patientNumber) {
    vaultRequestParams.append('patient_number', patientNumber);
  }
  paymentMethodIds.forEach((paymentMethodId) => {
    vaultRequestParams.append('payment_method_ids', paymentMethodId);
  });

  const response = await axios.get(`/api/v1/vault/${customerId}`, {
    headers: { Authorization: `Bearer ${computedRouteApiKey.value}` },
    params: vaultRequestParams,
  });
  let message = response.data?.message;
  console.log('new customerComputedAsync', message);
  return message;
}, null);

function b64ToUtf8(base64) {
  const asciiStr = atob(base64);
  return decodeURIComponent(
    asciiStr.split('').map((c) => {
      return '%' + c.charCodeAt(0).toString(16).padStart(2, '0');
    }).join('')
  );
}

function utf8ToB64(str) {
  const utf8Bytes = encodeURIComponent(str)
    .replace(/%([0-9A-F]{2})/g, (match, p1) =>
      String.fromCharCode('0x' + p1)
    );
  return btoa(utf8Bytes);
}

async function rerunLogic() {
  console.log('========== RERUN LOGIC ==========');

  // Store payment options at the function level to ensure they persist
  let savedPaymentOptions = null;

  try {
    if (!computedRouteApiKey.value) {
      throw new Error('API key is required');
    }

    // Wait for vault customer data if a customerId is present
    if (computedRouteInfo.value.customerId) {
      let attempts = 0;
      while (customerComputedAsync.value === null && attempts < 50) {
        console.log('Waiting for vault customer data...');
        // wait 100ms between checks, up to ~5 seconds total
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
    }

    // Wait for merchant data if needed
    if (!computedMerchantFromApiKey.value) {
      console.log('Merchant data not loaded yet, waiting...');

      let attempts = 0;
      while (!computedMerchantFromApiKey.value && attempts < 50) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      if (!computedMerchantFromApiKey.value) {
        console.error('Failed to load merchant data after 5 seconds');
        throw new Error('Could not load merchant information');
      }
    }

    console.log('Merchant data loaded:', computedMerchantFromApiKey.value);

    if (!url.value) {
      // Use the terminal ID if provided in the URL
      const params = terminalId.value ? `?id=${terminalId.value}` : '';

      const response = await axios.get(`/api/virtualterminalurl${params}`, {
        headers: { Authorization: `Bearer ${computedRouteApiKey.value}` }
      });

      // Handle response that could be an array or single object
      if (Array.isArray(response.data?.message)) {
        availableTerminals.value = response.data.message;

        // If multiple terminals returned and no specific ID requested, use the first active one
        if (!terminalId.value) {
          selectedTerminal.value = response.data.message.find(t => t.is_active) || response.data.message[0];

          if (selectedTerminal.value) {
            url.value = selectedTerminal.value.url || '';
            customFields.value = selectedTerminal.value.customFields || [];

            // Update the URL to include the terminal ID
            router.replace({
              query: { ...route.query, terminalId: selectedTerminal.value.id }
            });
          } else {
            // No terminals found
            toast.info("No virtual terminals found. Please configure one.", toastdefault);
            await router.push(`/configure-terminal/${computedRoutePublicKey.value}?routing=${computedRouteApiKey.value}&new=true`);
            return;
          }
        } else {
          // Find the specific terminal requested
          selectedTerminal.value = response.data.message.find(t => t.id === terminalId.value);

          if (!selectedTerminal.value) {
            toast.error("Terminal not found with that ID", toastdefault);

            // Fallback to first active terminal
            selectedTerminal.value = response.data.message.find(t => t.is_active) || response.data.message[0];

            if (selectedTerminal.value) {
              router.replace({
                query: { ...route.query, terminalId: selectedTerminal.value.id }
              });
              url.value = selectedTerminal.value.url || '';
              customFields.value = selectedTerminal.value.customFields || [];
            } else {
              await router.push(`/configure-terminal/${computedRoutePublicKey.value}?routing=${computedRouteApiKey.value}&new=true`);
              return;
            }
          } else {
            url.value = selectedTerminal.value.url || '';
            customFields.value = selectedTerminal.value.customFields || [];
          }
        }
      } else if (response.data?.message) {
        // Single terminal response
        selectedTerminal.value = response.data.message;
        availableTerminals.value = [response.data.message];
        url.value = response.data?.message?.url || '';
        customFields.value = response.data?.message?.customFields || [];

        console.log('Custom fields loaded:', customFields.value);
      } else {
        // No terminal data
        toast.info("Please configure your virtual terminal", toastdefault);
        await router.push(`/configure-terminal/${computedRoutePublicKey.value}?routing=${computedRouteApiKey.value}&new=true`);
        return;
      }
    }

    if (!url.value) {
      toast.success("Please Configure Your Virtual Terminal", toastdefault);
      await router.push(`/configure-terminal/${computedRoutePublicKey.value}?routing=${computedRouteApiKey.value}${terminalId.value ? `&terminalId=${terminalId.value}` : '&new=true'}`);
    }

    const urlObj = new URL(url.value);
    const newUrl = new URL(urlObj.pathname + urlObj.search, window.location.origin);

    const searchParams = new URLSearchParams(newUrl.search);
    const paramsBase64 = searchParams.get('params');
    const originalParams = JSON.parse(b64ToUtf8(paramsBase64));

    // CRITICAL: Save payment options immediately
    savedPaymentOptions = originalParams.paymentOptions ? [...originalParams.paymentOptions] : [];
    console.log('SAVED payment options from original URL:', savedPaymentOptions);
    console.log('Does saved options include newpos?', savedPaymentOptions.includes('newpos'));

    // Create fresh params object
    let paramsObj = JSON.parse(JSON.stringify(originalParams));

    // Update dynamic values
    userId.value = user.value.id;
    merchantId.value = computedMerchantFromApiKey.value?.id;

    console.log('Setting IDs for iframe:', {
      userId: userId.value,
      merchantId: merchantId.value
    });

    paramsObj.merchantId = merchantId.value;
    paramsObj.userId = userId.value;

    paramsObj.ccLabel = "Credit Card - Key Entry";
    paramsObj.showAsterisks = true;
    paramsObj.requireCVV = true;
    paramsObj.zipMode = "required";

    // Add terminal ID to params
    if (selectedTerminal.value?.id) {
      paramsObj.terminalId = selectedTerminal.value.id;
    }

    // Handle vault customer
    let vaultCustomer = customerComputedAsync.value;
    if (vaultCustomer) {
      paramsObj.vaultCustomer = vaultCustomer;
      let methods = vaultCustomer.methods || [];
      paramsObj.vaultCustomer.paymentMethodIdentifiers = methods.map(e => e.token);

      let sortedMethods = methods.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      let latestMethod;
      if (computedRouteInfo.value.paymentMethodId) {
        latestMethod = methods.filter(m => computedRouteInfo.value.paymentMethodId === m.id).pop();
      } else {
        latestMethod = sortedMethods.at(-1);
      }
      if (latestMethod) {
        paramsObj.vaultCustomer.latestMethod = latestMethod;
      }
    }

    console.log('Payment options before processUserPermissions:', paramsObj.paymentOptions);

    // Process user permissions
    let rbacResult = processUserPermissions(computedMerchantFromApiKey, merchantHasPermissions, store, paramsObj, toast, router, computedRouteInfo.value.isSwipeOnly);

    console.log('Payment options after processUserPermissions:', paramsObj.paymentOptions);

    let filteredMerchantId = user.value?.account?.memberships?.filter(m => m.merchant.id === merchantId.value);
    console.log(filteredMerchantId);
    console.log("filteredMerchantId value is above");

    let role = user.value?.account?.memberships?.filter(m => m.merchant.id === merchantId.value)?.map(e => e.role)?.pop();
    console.log(role);
    console.log("role is above");

    if (paramsBase64 && role && selectedTerminal.value?.id) {
      // Apply terminal-specific custom field permissions
      const terminalAccess = role.custom_field_access?.[selectedTerminal.value.id];

      if (terminalAccess) {
        // Filter and update custom fields based on role permissions for this specific terminal
        customFields.value = customFields.value.map(cf => {
          let vis = terminalAccess[cf.name];
          console.log(vis);
          console.log(cf.name);
          console.log("cf.name and visibility above");
          if (vis === 'require' || vis === 'show') {
            cf.required = vis === 'require';
            cf.requireForGuest = false;
            cf.is_hidden = false;
          } else {
            console.log(cf.name);
            console.log("cf.name WILL BE HIDDEN");
            cf.is_hidden = true;
          }
          return cf;
        }).filter(cf => !cf.is_hidden);
      } else {
        // No permissions defined for this terminal - hide all custom fields
        customFields.value = [];
        console.log(`No custom field permissions defined for terminal ${selectedTerminal.value.id}`);
      }

      paramsObj.customFields = customFields.value;

      if (role.granted_permissions) {
        if (
          role.granted_permissions.some(
            (permission) => permission.name === 'CC Zip'
          )
        ) {
          console.log('User has CC Zip Override permission');
          paramsObj.zipMode = null;
        }
      }
    } else {
      // No role or terminal-specific permissions, use default custom fields
      paramsObj.customFields = customFields.value;
    }

    // Add isSwipeAndCNP options if needed
    if (computedRouteInfo.value.isSwipeAndCNP) {
      const additionalOptions = ['credit_card', 'newpos', 'token'];
      additionalOptions.forEach(opt => {
        if (!paramsObj.paymentOptions.includes(opt)) {
          paramsObj.paymentOptions.push(opt);
        }
      });
    }

    console.log('FINAL FORCED payment options:', paramsObj.paymentOptions);
    console.log('Includes newpos?', paramsObj.paymentOptions?.includes('newpos'));

    // reset the overflow style
    paramsObj.overflowStyle = null;

    // enable events to fire
    paramsObj.fromLink = false;
    paramsObj.shouldRedirectOnSuccess = true;

    const patientQueryKeys = ['patient_first_name', 'patient_last_name', 'patient_number', 'payment_method_ids'];
    patientQueryKeys.forEach((key) => {
      const value = computedRouteInfo.value[key];
      if (Array.isArray(value)) {
        if (value.length) {
          searchParams.delete(key);
          value.forEach((item) => searchParams.append(key, item));
        }
        return;
      }
      if (value !== undefined && value !== null && value !== '') {
        searchParams.set(key, value);
      }
    });

    const updatedParamsBase64 = utf8ToB64(JSON.stringify(paramsObj));
    searchParams.set('params', updatedParamsBase64);
    newUrl.search = searchParams.toString();
    iframeLink.value = newUrl.href;

    // Final verification
    const finalVerification = JSON.parse(b64ToUtf8(updatedParamsBase64));
    console.log('FINAL VERIFICATION - Params sent to iframe:', finalVerification);
    console.log('FINAL VERIFICATION - Payment options:', finalVerification.paymentOptions);
    console.log('FINAL VERIFICATION - Includes newpos?', finalVerification.paymentOptions?.includes('newpos'));

  } catch (error) {
    console.error('Error:', error);
    params.value = utf8ToB64(JSON.stringify({ apikey: computedRoutePublicKey.value, isVt: true }));
    iframeLink.value = `${window.location.origin}/responsive-iframe/payment?params=${params.value}`;
  } finally {
    loading.value = false;
  }
}

// Function to switch terminals
async function switchTerminal(newTerminalId) {
  if (newTerminalId === terminalId.value) {
    return; // Already on this terminal
  }

  loading.value = true;

  // Update the URL
  await router.push({
    query: { ...route.query, terminalId: newTerminalId }
  });

  // Reload the terminal data
  url.value = ''; // Force reload
  await rerunLogic();
}

console.log('Permissions Rendered:', permissions.value);

// Improved onMounted that waits for required data
onMounted(async () => {
  console.log('Component mounted, waiting for required data...');

  // Wait for all required data to be available
  const checkInterval = setInterval(() => {
    const hasRequiredData = !!(
      computedRouteApiKey.value &&
      user.value?.id &&
      computedMerchantFromApiKey.value?.id &&
      permissions.value !== 'FETCHING'
    );

    console.log('Checking required data:', {
      routeApiKey: !!computedRouteApiKey.value,
      userId: !!user.value?.id,
      merchantId: !!computedMerchantFromApiKey.value?.id,
      merchantData: computedMerchantFromApiKey.value,
      permissionsLoaded: permissions.value !== 'FETCHING'
    });

    if (hasRequiredData) {
      clearInterval(checkInterval);
      console.log('All required data loaded, running logic');
      rerunLogic();
    }
  }, 100);

  // Timeout after 10 seconds
  setTimeout(() => {
    clearInterval(checkInterval);
    if (!computedMerchantFromApiKey.value?.id) {
      console.error('Timeout waiting for required data');
      toast.error('Failed to load merchant information', toastdefault);
    }
    loading.value = false;
  }, 10000);
});

onMounted(() => {
  window.addEventListener('message', handleIframeMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', handleIframeMessage);
});

// Only watch for terminal changes (user manually switching)
watch(terminalId, async (newVal, oldVal) => {
  if (oldVal && newVal !== oldVal) {
    console.log('Terminal ID changed by user');
    url.value = '';
    await rerunLogic();
  }
});

</script>
<template>
  <div class="virtual-terminal-background card-2">
    <div class="top-panel d-flex align-items-center">
      <button class="btn" @click="handleBackClick">
        <svg
          viewBox="0 0 16 16"
          class="bi bi-arrow-left"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            d="M15 8a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 0 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 0 1 .708.708L2.707 7.5H14.5A.5.5 0 0 1 15 8z"
          />
        </svg>
        {{ backButtonLabel }}
      </button>
      <div class="w-100 d-flex" style="flex-direction: column;">
        <div class="ms-auto me-auto mt-2">
          <span class="dashboard-title">Virtual Terminal</span>
        </div>
      </div>
      <div class="d-flex align-items-center gap-2">
<!--        &lt;!&ndash; Terminal Selector Dropdown &ndash;&gt;-->
<!--        <div v-if="availableTerminals.length > 1" class="dropdown">-->
<!--          <button-->
<!--            class="btn btn-secondary dropdown-toggle"-->
<!--            type="button"-->
<!--            data-bs-toggle="dropdown"-->
<!--            aria-expanded="false"-->
<!--          >-->
<!--            Switch Terminal-->
<!--          </button>-->
<!--          <ul class="dropdown-menu">-->
<!--            <li v-for="terminal in availableTerminals" :key="terminal.id">-->
<!--              <a-->
<!--                class="dropdown-item"-->
<!--                :class="{ active: terminal.id === terminalId }"-->
<!--                href="#"-->
<!--                @click.prevent="switchTerminal(terminal.id)"-->
<!--              >-->
<!--                {{ terminal.name || 'Default Terminal' }}-->
<!--                {{ terminal.is_active ? '' : '(Inactive)' }}-->
<!--              </a>-->
<!--            </li>-->
<!--          </ul>-->
<!--        </div>-->
      </div>
    </div>
    <div v-if="loading || permissions === 'FETCHING'">
      <BaseSpinner />
    </div>

    <div v-else>
      <div v-if="isMissingRbacPermissions">
        <div id="shroud" style="position: relative; pointer-events: none;">
          <div id="shroud-inner" style="position: absolute; width: 100%; height: 100vh; background-color: rgba(255,181,181,0.17); border-radius: 25px;">
          </div>
          <iframe
            :src="iframeLink"
            id="payment-iframe"
            ref="paymentIframe"
            allow="payment 'self'"
            style="width: 100%; height: 100vh"
            frameborder="0"
          >
          </iframe>
        </div>
      </div>
      <div v-else>
        <iframe
          :src="iframeLink"
          id="payment-iframe"
          ref="paymentIframe"
          style="width: 100%; height: 100vh"
          allow="payment 'self'"
          frameborder="0"
        >
        </iframe>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
.virtual-terminal-background {
  background: #fff !important;
  border-radius: 5px;
}
.top-panel {
  display: flex;
  /* justify-content: space-between;
  align-items: center; */
  .gap-2 {
    gap: 0.5rem;
  }
}
.dashboard-title {
  font-size: 24px;
  color: #383838;
  /* margin-bottom: 10px; */
}
</style>
